import api from "../api";

const propertyMonthlyInspectionsService = {
  // Get form data for creating monthly inspection report
  getFormData: async (propertyId) => {
    return await api.get(
      `/monthlyinspectionreports/properties/${propertyId}/form-data`
    );
  },

  // Get monthly inspections for a property
  getMonthlyInspections: async (propertyId) => {
    return await api.get(`/properties/${propertyId}/monthly-inspections`);
  },

  // Get monthly inspection report details
  getMonthlyInspectionReport: async (reportId) => {
    return await api.get(`/monthlyinspectionreports/${reportId}`);
  },

  // Convert YYYY-MM-DD to d/m/Y format (Laravel format)
  convertToLaravelDateFormat: (dateString) => {
    if (!dateString) return "";
    // If already in d/m/Y format, return as is
    if (dateString.includes("/") && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
      return dateString;
    }
    // Convert from YYYY-MM-DD (ISO format from date input) to d/m/Y
    if (dateString.includes("-")) {
      const [year, month, day] = dateString.split("-");
      return `${day}/${month}/${year}`;
    }
    return dateString;
  },

  // Create new monthly inspection report
  createMonthlyInspectionReport: async (propertyId, formData) => {
    const formDataToSend = new FormData();

    // Add all form data
    Object.keys(formData).forEach((key) => {
      if (formData[key] !== null && formData[key] !== undefined) {
        let value = formData[key];
        
        // Convert date fields from YYYY-MM-DD to d/m/Y format (Laravel format)
        if (key === "inspection_date" || key === "lease_start_date") {
          value = propertyMonthlyInspectionsService.convertToLaravelDateFormat(value);
        }
        
        // EMERGENCY FIX: Handle reading fields arrays specially
        if (
          key === "reading_fields" ||
          key === "reading_fields_names" ||
          key === "reading_fields_serials"
        ) {
          // Send arrays as individual form fields for proper backend processing
          if (Array.isArray(value)) {
            value.forEach((item, index) => {
              formDataToSend.append(`${key}[${index}]`, item);
            });
          }
        } else if (
          typeof value === "object" &&
          !(value instanceof File)
        ) {
          formDataToSend.append(key, JSON.stringify(value));
        } else {
          formDataToSend.append(key, value);
        }
      }
    });

    return await api.post(
      `/monthlyinspectionreports/properties/${propertyId}`,
      formDataToSend,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
  },

  // Update monthly inspection report
  updateMonthlyInspectionReport: async (reportId, formData) => {
    const formDataToSend = new FormData();

    // Add all form data
    Object.keys(formData).forEach((key) => {
      if (formData[key] !== null && formData[key] !== undefined) {
        let value = formData[key];
        
        // Skip e_sign if it's a presigned URL (http:// or https://) - backend will preserve existing signature
        // Only send e_sign if it's a new base64 signature (data:image/...) or an S3 key
        if (key === "e_sign") {
          // If it's a presigned URL, don't send it - backend will preserve existing S3 key
          if (typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://'))) {
            console.log('⚠️ [FRONTEND] Skipping presigned URL for e_sign, backend will preserve existing signature');
            return; // Skip this field
          }
          // If it's a base64 signature or S3 key, send it
          console.log('✅ [FRONTEND] Sending e_sign (base64 or S3 key)');
        }
        
        // Convert date fields from YYYY-MM-DD to d/m/Y format (Laravel format)
        if (key === "inspection_date" || key === "lease_start_date") {
          value = propertyMonthlyInspectionsService.convertToLaravelDateFormat(value);
        }
        
        // EMERGENCY FIX: Handle reading fields arrays specially
        if (
          key === "reading_fields" ||
          key === "reading_fields_names" ||
          key === "reading_fields_serials"
        ) {
          // Send arrays as individual form fields for proper backend processing
          if (Array.isArray(value)) {
            value.forEach((item, index) => {
              formDataToSend.append(`${key}[${index}]`, item);
            });
          }
        } else if (
          typeof value === "object" &&
          !(value instanceof File)
        ) {
          formDataToSend.append(key, JSON.stringify(value));
        } else {
          formDataToSend.append(key, value);
        }
      }
    });

    return await api.put(
      `/monthlyinspectionreports/${reportId}`,
      formDataToSend,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
  },

  // Delete monthly inspection report
  deleteMonthlyInspectionReport: async (reportId) => {
    return await api.delete(`/monthlyinspectionreports/${reportId}`);
  },

  // Remove image from monthly inspection report
  removeImage: async (reportId, field) => {
    return await api.delete(
      `/monthlyinspectionreports/${reportId}/images/${field}`
    );
  },

  // Download report PDF
  downloadReportPDF: async (reportId) => {
    return await api.get(`/monthlyinspectionreports/${reportId}/download-pdf`, {
      responseType: 'blob'
    });
  },

  // Legacy methods (keeping for backward compatibility)
  getPropertyDetails: async (propertyId) => {
    return await api.get(`/properties/${propertyId}`);
  },

  toggleIgnore: async (propertyId, year, month, monthNumber, ignored) => {
    return await api.post(
      `/properties/${propertyId}/monthly-inspections/ignore`,
      {
        year: year,
        month: month,
        monthNumber: monthNumber,
        ignored: ignored,
      }
    );
  },
};

export default propertyMonthlyInspectionsService;
